import { useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileUp, Wand2 } from "lucide-react";
import { readWorkbookRaw, detectHeaderRow, autoMap, toISODate } from "@/lib/excel";
import { usePinPrompt } from "@/components/app/PinPrompt";

export const IMPORT_FIELDS = {
  parties: {
    name: ["name", "party name", "party", "customer name", "supplier name", "customer", "supplier", "ledger name", "ledger", "account name", "firm name", "company name", "vendor"],
    kind: ["type", "kind", "party type", "category", "group", "under"],
    phone: ["phone", "mobile", "mobile no", "contact", "contact no", "phone no", "whatsapp", "cell"],
    email: ["email", "email id", "e-mail", "mail"],
    address: ["address", "billing address", "addr", "city", "location"],
    gstin: ["gstin", "gst no", "gst number", "gstin/uin", "gst", "tax id"],
    credit_period_days: ["credit days", "credit period", "credit period days", "due days", "payment terms"],
    opening_balance: ["opening balance", "opening", "balance", "op balance", "outstanding", "op bal"],
    opening_type: ["dr/cr", "balance type", "opening type", "receivable/payable"],
  },
  items: {
    name: ["name", "item name", "item", "product", "product name", "stock item", "description", "particulars", "material", "goods"],
    item_type: ["item type", "goods/service", "nature"],
    sku: ["sku", "item code", "code", "product code", "part no", "model"],
    barcode: ["barcode", "ean", "upc", "bar code"],
    category: ["category", "group", "stock group", "brand", "under"],
    unit: ["unit", "uom", "units", "unit of measure", "base unit"],
    sale_price: ["sale price", "selling price", "mrp", "rate", "sales rate", "price", "sp"],
    purchase_price: ["purchase price", "cost", "cost price", "purchase rate", "buying price", "cp"],
    gst_rate: ["gst", "gst rate", "gst %", "tax rate", "tax %", "igst rate", "gst%"],
    hsn: ["hsn", "hsn code", "hsn/sac", "sac"],
    stock: ["stock", "qty", "quantity", "opening stock", "opening qty", "closing stock", "balance qty", "in stock"],
    min_stock: ["min stock", "minimum stock", "min qty"],
    reorder_level: ["reorder level", "reorder", "reorder qty"],
    batch_no: ["batch", "batch no", "lot", "lot no"],
    expiry_date: ["expiry", "expiry date", "exp date", "exp", "best before", "use by"],
  },
  invoices: {
    invoice_no: ["invoice no", "invoice number", "bill no", "voucher no", "inv no", "number", "invoice", "bill number", "ref no"],
    date: ["date", "invoice date", "bill date", "voucher date", "txn date"],
    party_name: ["party name", "party", "customer", "supplier", "customer name", "supplier name", "buyer", "ledger", "name"],
    gstin: ["gstin", "gst no", "gstin/uin"],
    item_name: ["item name", "item", "product", "description", "particulars", "stock item", "goods"],
    qty: ["qty", "quantity", "nos", "units"],
    unit: ["unit", "uom"],
    price: ["price", "rate", "unit price", "mrp", "selling price"],
    amount: ["amount", "value", "total", "line total", "taxable value", "net amount"],
    discount: ["discount", "disc", "discount amount"],
    gst_rate: ["gst", "gst rate", "gst %", "tax rate", "tax %"],
    hsn: ["hsn", "hsn code", "hsn/sac"],
    batch_no: ["batch", "batch no", "lot"],
    expiry_date: ["expiry", "expiry date", "exp date"],
    paid: ["paid", "amount paid", "received", "payment"],
    payment_mode: ["payment mode", "mode", "pay mode"],
    notes: ["notes", "narration", "remarks", "memo"],
  },
};

const REQUIRED = { parties: ["name"], items: ["name"], invoices: ["item_name"] };
const num = (v) => { const n = parseFloat(String(v ?? "").replace(/[^\d.-]/g, "")); return isNaN(n) ? 0 : n; };

function buildRecords(rows, header, map) {
  return rows.slice(header + 1).map((r) => {
    const o = {};
    for (const [f, idx] of Object.entries(map)) if (idx !== undefined && idx !== "") o[f] = r[idx];
    return o;
  }).filter((o) => Object.values(o).some((v) => String(v ?? "").trim()));
}

function groupInvoices(records) {
  const groups = new Map();
  records.forEach((r, i) => {
    if (!String(r.item_name ?? "").trim()) return;
    const key = String(r.invoice_no ?? "").trim() || `ROW-${i}`;
    const qty = num(r.qty) || 1;
    const price = num(r.price) || (num(r.amount) ? num(r.amount) / qty : 0);
    const line = { name: String(r.item_name).trim(), qty, unit: String(r.unit || "PCS"), price: +price.toFixed(2),
      discount: num(r.discount), gst_rate: num(r.gst_rate), hsn: r.hsn ? String(r.hsn) : null,
      batch_no: r.batch_no ? String(r.batch_no) : null, expiry_date: r.expiry_date ? toISODate(r.expiry_date)?.slice(0, 10) : null };
    if (!groups.has(key)) {
      groups.set(key, { invoice_no: key.startsWith("ROW-") ? null : key, date: toISODate(r.date) || new Date().toISOString(),
        party_name: String(r.party_name || "").trim(), gstin: r.gstin ? String(r.gstin) : null,
        paid: num(r.paid), payment_mode: String(r.payment_mode || (num(r.paid) ? "cash" : "credit")).toLowerCase(),
        notes: r.notes ? String(r.notes) : null, gst_enabled: false, lines: [] });
    }
    const g = groups.get(key);
    g.lines.push(line);
    if (line.gst_rate > 0) g.gst_enabled = true;
  });
  return Array.from(groups.values());
}

export default function SmartImport({ target, txnType, open, onClose, onDone }) {
  const kind = target === "invoices" ? "invoices" : target;
  const fields = IMPORT_FIELDS[kind];
  const pinPrompt = usePinPrompt();
  const [sheets, setSheets] = useState(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [header, setHeader] = useState(0);
  const [map, setMap] = useState({});
  const [updateExisting, setUpdateExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const rows = sheets?.[sheetIdx]?.rows || [];
  const headers = (rows[header] || []).map((h, i) => String(h || `Column ${i + 1}`));

  const pickSheet = (all, i) => {
    const h = detectHeaderRow(all[i].rows, fields);
    setSheetIdx(i); setHeader(h); setMap(autoMap(all[i].rows[h] || [], fields));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try {
      const all = await readWorkbookRaw(file);
      if (!all.length || !all.some((s) => s.rows.length)) return toast.error("File is empty");
      const first = Math.max(0, all.findIndex((s) => s.rows.length > 1));
      setSheets(all); setResult(null); pickSheet(all, first);
    } catch (err) { toast.error("Could not read file: " + (err?.message || err)); }
  };

  const records = sheets ? buildRecords(rows, header, map) : [];
  const missing = REQUIRED[kind].filter((f) => map[f] === undefined || map[f] === "");

  const doImport = async () => {
    if (missing.length) return toast.error(`Map required column: ${missing.join(", ")}`);
    await pinPrompt();
    setBusy(true);
    try {
      let data;
      if (kind === "invoices") {
        ({ data } = await api.post("/import/invoices", { txn_type: txnType, invoices: groupInvoices(records) }));
      } else {
        ({ data } = await api.post(`/import/${kind}`, { rows: records, update_existing: updateExisting }));
      }
      setResult(data);
      toast.success(`Imported: ${data.created} created${data.updated ? `, ${data.updated} updated` : ""}`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const close = () => { setSheets(null); setResult(null); onClose(); };
  const preview = kind === "invoices" ? groupInvoices(records) : records;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="smart-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 size={16} /> Smart Import — {kind === "invoices" ? txnType : kind}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground">Upload any Excel / CSV — column names and order don't matter. We detect the header row and match columns automatically; adjust anything below before importing.</div>
        <label className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-5 cursor-pointer hover:bg-slate-50" data-testid="smart-import-drop">
          <FileUp size={16} /> {sheets ? "Choose another file" : "Choose Excel / CSV file"}
          <input type="file" accept=".xlsx,.xls,.csv,.ods" className="hidden" onChange={onFile} data-testid="smart-import-file" />
        </label>
        {sheets && (
          <div className="space-y-4 mt-2">
            <div className="flex gap-4 flex-wrap items-end">
              {sheets.length > 1 && (
                <div><Label>Sheet</Label>
                  <select className="border border-border rounded-md h-9 px-2 bg-white block" value={sheetIdx} onChange={(e) => pickSheet(sheets, +e.target.value)} data-testid="smart-import-sheet">
                    {sheets.map((s, i) => <option key={s.name} value={i}>{s.name} ({s.rows.length} rows)</option>)}
                  </select>
                </div>
              )}
              <div><Label>Header row</Label>
                <select className="border border-border rounded-md h-9 px-2 bg-white block" value={header} onChange={(e) => { const h = +e.target.value; setHeader(h); setMap(autoMap(rows[h] || [], fields)); }} data-testid="smart-import-header">
                  {rows.slice(0, 30).map((r, i) => <option key={`h-${i}`} value={i}>Row {i + 1}: {r.filter(Boolean).slice(0, 4).join(" | ").slice(0, 60)}</option>)}
                </select>
              </div>
              {kind !== "invoices" && (
                <div className="flex items-center gap-2 pb-2"><Switch checked={updateExisting} onCheckedChange={setUpdateExisting} data-testid="smart-import-update" /><Label>Update existing records with the same name</Label></div>
              )}
            </div>
            <div>
              <div className="overline mb-2">Column mapping</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.keys(fields).map((f) => (
                  <div key={f}>
                    <Label className="text-xs">{f.replace(/_/g, " ")}{REQUIRED[kind].includes(f) && " *"}</Label>
                    <select className={`w-full border rounded-md h-8 px-2 bg-white text-sm ${map[f] !== undefined ? "border-emerald-500" : "border-border"}`}
                      value={map[f] ?? ""} onChange={(e) => setMap({ ...map, [f]: e.target.value === "" ? undefined : +e.target.value })} data-testid={`map-${f}`}>
                      <option value="">— skip —</option>
                      {headers.map((h, i) => <option key={`c-${i}`} value={i}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-2">Preview ({preview.length} {kind === "invoices" ? "invoices" : "rows"})</div>
              <div className="max-h-56 overflow-auto border border-border rounded-md">
                <table className="acc-table w-full text-xs">
                  <thead><tr>{kind === "invoices" ? <><th>Invoice</th><th>Date</th><th>Party</th><th className="num">Lines</th><th className="num">Value</th></> : Object.keys(fields).filter((f) => map[f] !== undefined).map((f) => <th key={f}>{f}</th>)}</tr></thead>
                  <tbody>
                    {preview.slice(0, 10).map((r, i) => kind === "invoices" ? (
                      <tr key={`p-${i}`}><td>{r.invoice_no || "—"}</td><td>{new Date(r.date).toLocaleDateString()}</td><td>{r.party_name || "—"}</td><td className="num">{r.lines.length}</td><td className="num">{fmtINR(r.lines.reduce((s, l) => s + l.qty * l.price - l.discount, 0))}</td></tr>
                    ) : (
                      <tr key={`p-${i}`}>{Object.keys(fields).filter((f) => map[f] !== undefined).map((f) => <td key={f}>{String(r[f] instanceof Date ? r[f].toLocaleDateString() : r[f] ?? "")}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="smart-import-result">
                Created {result.created} · {result.updated !== undefined && `Updated ${result.updated} · Skipped ${result.skipped} · `}Failed {result.failed}
                {result.errors?.length > 0 && <ul className="list-disc ml-4 mt-1 text-xs text-amber-800">{result.errors.slice(0, 10).map((e, i) => <li key={`e-${i}`}>{e}</li>)}</ul>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Close</Button>
              <Button className="btn-primary" onClick={doImport} disabled={busy || !preview.length} data-testid="smart-import-confirm">{busy ? "Importing…" : `Import ${preview.length}`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
